/* eslint-disable no-warning-comments */
import * as React from 'react'

import ArcadeEmbed from '@/components/ArcadeEmbed/ArcadeEmbed'
import Article from '@/components/Article/Article'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb'
import CallToAction from '@/components/CallToAction/CallToAction'
import CardsBanner from '@/components/CardsBanner'
import ComparisonDropdown from '@/components/Comparison/ComparisonDropdown'
import ComparisonLinks from '@/components/Comparison/ComparisonLinks'
import ComparisonTable from '@/components/Comparison/ComparisonTable'
import ContentCenter from '@/components/ContentCenter'
import ContentClassic from '@/components/ContentClassic'
import ContentColumns from '@/components/ContentColumns'
import CtaCard from '@/components/CtaCard'
import FAQ from '@/components/FAQ/FAQ'
import FeaturedPage from '@/components/FeaturedPage'
import Features from '@/components/Features'
import FeaturesSlider from '@/components/FeaturesSlider'
import Footer from '@/components/Footer/Footer'
import Form from '@/components/Form'
import GlobalComponent from '@/components/GlobalComponent'
import Hero from '@/components/Hero'
import HeroHomepage from '@/components/HeroHomepage'
import KeyFigures from '@/components/KeyFigures'
import KeyPoints from '@/components/KeyPoints'
import Logos from '@/components/Logos'
import LogosFiltered from '@/components/LogosFiltered'
import Navigation from '@/components/Navigation'
import NavigationV2 from '@/components/NavigationV2'
import PagesList from '@/components/PagesList'
import ScrollingTitle from '@/components/ScrollingTitle'
import Tabs from '@/components/Tabs'
import Team from '@/components/Team'
import TeamSlider from '@/components/TeamSlider'
import Testimonials from '@/components/Testimonials'
import Video from '@/components/Video'

const CardsSlider = React.lazy(() => import('@/components/CardsSlider'))
const Pricing = React.lazy(() => import('@/components/Pricing'))

const resolveGlobalComponent = (props) => {
  if (
    props.component === 'global-component' &&
    props.reference &&
    props.reference.content &&
    Array.isArray(props.reference.content.components) &&
    props.reference.content.components[0]
  ) {
    return props.reference.content.components[0]
  }

  return props
}

const componentsUsingPrev = ['key-figures', 'call-to-action']
const componentsUsingNext = []

const getBackgroundColor = (props = {}, {prev, next} = {prev: false, next: false}) => {
  if (prev && Array.isArray(props.components) && typeof props.index === 'number') {
    const prevComponent = props.components[props.index - 1]
    if (prevComponent) props = resolveGlobalComponent(prevComponent)
  } else if (next && Array.isArray(props.components) && typeof props.index === 'number') {
    const nextComponent = props.components[props.index + 1]
    if (nextComponent) props = resolveGlobalComponent(nextComponent)
  }

  // If our component uses next color and next component uses prev color
  // Use a neutral color
  if (
    (next && componentsUsingPrev.includes(props.component)) ||
    (prev && componentsUsingNext.includes(props.component))
  ) {
    return 'bg-violet-600'
  }

  switch (props.backgroundColor) {
    case 'yellow-400':
      return 'bg-yellow-400'
    case 'turquoise-400':
      return 'bg-turquoise-400'
    case 'violet-400':
      return 'bg-violet-400'
    default:
  }

  switch (props.component) {
    case 'cards-slider':
      return 'bg-purple-900'
    case 'big-card':
      return 'bg-violet-400'
    case 'key-figures':
      return 'bg-violet-600'
    case 'features-slider':
    case 'ressource-form':
      return 'bg-yellow-200'
    case 'call-to-action':
    case 'featured-page':
    case 'footer':
      return 'bg-yellow-400'
    case 'article':
    case 'breadcrumb':
    case 'faq':
    case 'features':
    case 'content-classic':
    case 'team':
    case 'content-columns':
    case 'cards-banner':
      return 'bg-white'
    default:
      return 'bg-transparent'
  }
}

const DynamicComponent = (props) => {
  const extendedProps = {
    ...props,
    backgroundColors: {
      previous: getBackgroundColor(props, {prev: true}),
      current: getBackgroundColor(props),
      next: getBackgroundColor(props, {next: true}),
    },
  }

  switch (props.component) {
    case 'article':
      return <Article {...extendedProps} />
    case 'arcade-embed':
      return <ArcadeEmbed {...extendedProps} />
    case 'breadcrumb':
      return <Breadcrumb {...extendedProps} />
    case 'call-to-action':
      return <CallToAction {...extendedProps} />
    case 'cards-banner':
      return <CardsBanner {...extendedProps} />
    case 'cards-slider':
      return <CardsSlider {...extendedProps} />
    case 'comparison-dropdown':
      return <ComparisonDropdown {...extendedProps} />
    case 'comparison-links':
      return <ComparisonLinks {...extendedProps} />
    case 'comparison-table':
      return <ComparisonTable {...extendedProps} />
    case 'content-classic':
      return <ContentClassic {...extendedProps} />
    case 'content-center':
      return <ContentCenter {...extendedProps} />
    case 'content-columns':
      return <ContentColumns {...extendedProps} />
    case 'cta-card':
      return <CtaCard {...extendedProps} />
    case 'faq':
      return <FAQ {...extendedProps} />
    case 'featured-page':
      return <FeaturedPage {...extendedProps} />
    case 'features':
      return <Features {...extendedProps} />
    case 'features-slider':
      return <FeaturesSlider {...extendedProps} />
    case 'footer':
      return <Footer {...extendedProps} />
    case 'form':
      return <Form {...extendedProps} title={props.story.name} />
    case 'hero-homepage':
      return <HeroHomepage {...extendedProps} />
    case 'global-component':
      return <GlobalComponent {...extendedProps} />
    case 'hero':
      return <Hero {...extendedProps} />
    case 'key-figures':
      return <KeyFigures {...extendedProps} />
    case 'key-points':
      return <KeyPoints {...extendedProps} />
    case 'logos':
      return <Logos {...extendedProps} />
    case 'logos-filtered':
      return <LogosFiltered {...extendedProps} />
    case 'navigation':
      return <Navigation {...extendedProps} />
    case 'navigation-v2':
      return <NavigationV2 {...extendedProps} />
    case 'pages-list':
      return <PagesList {...extendedProps} />
    case 'pricing':
      return <Pricing {...extendedProps} />
    case 'scrolling-title':
      return <ScrollingTitle {...extendedProps} />
    case 'team':
      return <Team {...extendedProps} />
    case 'team-slider':
      return <TeamSlider {...extendedProps} />
    case 'testimonials':
      return <Testimonials {...extendedProps} />
    case 'tabs':
      return <Tabs {...extendedProps} />
    case 'video':
      return <Video {...extendedProps} />
    default:
      return <div>The component {props.component} has not been created yet.</div>
  }
}

export default DynamicComponent
